import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Pin, Heart, ChevronsUpDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import GlassPill from '@/components/ui/GlassPill';
import MoveCardButton from '@/components/ui/MoveCardButton';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { AppItem, useAppFavorites } from '@/hooks/app-library/use-app-favorites';
import { useAppIdeaFavorites } from '@/hooks/app-library/use-app-idea-favorites';
import { usePinnedAppsWithDetails } from '@/hooks/app-library/use-user-pinned-apps';
import { usePinMode } from '@/hooks/app-library/use-pin-mode';
import { getIconByName, formatAppStatus } from '@/hooks/app-library/utils';
import PersistentFavoriteButton from '@/components/ui/PersistentFavoriteButton';
import { useIsMobile } from '@/hooks/layout/use-mobile';
import { getActualRoute } from '@/utils/routeMapper';
import { useDragContext } from '@/contexts/drag/DragContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface AppCardProps {
  app: AppItem;
  isPinModeActive?: boolean;
  isPinnedPreview?: boolean;
  pinnedPosition?: 1 | 2 | 3 | 4;
  dragHandleRef?: (element: HTMLElement | null) => void;
  dragListeners?: SyntheticListenerMap;
  dragAttributes?: DraggableAttributes;
  isGreyedOut?: boolean;
  isAppIdea?: boolean;
  onAppIdeaClick?: (app: AppItem) => void;
}

const AppCard: React.FC<AppCardProps> = ({ 
  app, 
  isPinModeActive = false, 
  isPinnedPreview = false, 
  pinnedPosition: propPinnedPosition,
  dragHandleRef,
  dragListeners,
  dragAttributes,
  isGreyedOut = false,
  isAppIdea = false,
  onAppIdeaClick
}) => {
  const navigate = useNavigate();
  const { isAppFavorited, handleToggleFavorite, getAppFavoriteCount, isLoading } = useAppFavorites();
  const { isAppIdeaFavorited, handleToggleFavorite: handleToggleAppIdeaFavorite, getAppIdeaFavoriteCount, isLoading: isAppIdeaLoading } = useAppIdeaFavorites();
  const { isAppPinned } = usePinnedAppsWithDetails();
  const { handlePinApp, isUpdating: isPinUpdating } = usePinMode();
  const isMobile = useIsMobile();
  const { isDragging } = useDragContext();

  const handleToggleAppFavorite = async (itemId: string) => {
    if (isAppIdea) {
      await handleToggleAppIdeaFavorite(itemId);
    } else {
      await handleToggleFavorite(itemId);
    }
  };

  const isFavorited = isAppIdea ? isAppIdeaFavorited(app.id) : isAppFavorited(app.id);
  const favoriteCount = isAppIdea ? getAppIdeaFavoriteCount(app.id) : getAppFavoriteCount(app.id);
  const favoritesLoading = isAppIdea ? isAppIdeaLoading : isLoading;

  // App Idea development progress calculations
  const DEVELOPMENT_THRESHOLD = 100;
  const progressPercentage = isAppIdea ? Math.min((favoriteCount / DEVELOPMENT_THRESHOLD) * 100, 100) : 0;
  const likesToGo = isAppIdea ? Math.max(DEVELOPMENT_THRESHOLD - favoriteCount, 0) : 0;
  const isReadyForDevelopment = isAppIdea && favoriteCount >= DEVELOPMENT_THRESHOLD;

  const IconComponent = getIconByName(app.icon_name);
  const detectedPinnedPosition = isAppPinned(app.id);
  const isPinned = detectedPinnedPosition !== null;

  const handleCardClick = () => {
    // Prevent navigation during drag operations
    if (isDragging) return;

    if (isAppIdea && onAppIdeaClick) {
      onAppIdeaClick(app);
      return;
    }
    
    // Only allow navigation for active apps
    if (app.status === 'active') {
      navigate(getActualRoute(app.route));
    }
  };

  const getPinPositionBadge = () => {
    const positionToShow = isPinnedPreview ? propPinnedPosition : propPinnedPosition || detectedPinnedPosition;
    if (!positionToShow) return null;
    
    const positions = ['1st', '2nd', '3rd', '4th'];
    return (
      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg">
        <div className="flex items-center justify-center">
          <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
        </div>
        <span className="text-sm font-medium text-white">{positions[positionToShow - 1]}</span>
      </div>
    );
  };

  const cardClasses = cn(
    "group relative rounded-lg overflow-hidden h-full flex flex-col",
    "shadow-sm hover:shadow-md hover:shadow-amber-100/30 dark:hover:shadow-amber-900/20",
    isAppIdea ? "cursor-pointer" : (app.status === 'active' && "cursor-pointer"),
    app.status === 'coming-soon' && "opacity-80 cursor-default border border-white/20 dark:border-gray-700/50",
    app.status === 'proposed' && "opacity-80 cursor-default border border-white/20 dark:border-gray-700/50",
    app.status === 'featured' && "cursor-default",
    isPinModeActive && "border-2 border-brandBlue/20 dark:border-brandPurple/20",
    isPinned && isPinModeActive && "bg-gradient-to-br from-brandBlue/5 to-brandPurple/5",
    isPinnedPreview && "bg-gradient-to-br from-brandBlue/10 to-brandPurple/10 border-2 border-brandBlue/30 dark:border-brandPurple/30",
    isGreyedOut && "opacity-50"
  );

  return (
    <div 
      className={cardClasses}
      onClick={isAppIdea ? handleCardClick : (app.status === 'active' ? handleCardClick : undefined)}
      style={{ 
        borderRadius: '0.5rem',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform, box-shadow',
        transformStyle: 'preserve-3d',
        WebkitFontSmoothing: 'antialiased'
      }}
    >
        <div 
          className="absolute inset-0 bg-gradient-to-br from-white/40 via-white/20 to-white/5 dark:from-black/60 dark:to-black/30 backdrop-blur-xl backdrop-saturate-150 -z-10 will-change-transform group-hover:from-white/70 group-hover:via-white/40 group-hover:to-white/20 dark:group-hover:from-black/40 dark:group-hover:to-black/10" 
          style={{ 
            transform: 'translateZ(0)',
            borderRadius: 'inherit'
          }}
        />
        
        <div 
          className={`${isMobile ? 'p-3 pb-4' : 'p-5 pb-6'} flex flex-col h-full`} 
          style={{ 
            willChange: 'contents',
            borderRadius: 'inherit'
          }}
        >
          <div className="flex items-start justify-between mb-2 md:mb-3">
            <div className="flex items-center gap-2 md:gap-3">
              <div className={cn(
                "p-2 md:p-3 rounded-lg",
                "bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900",
                "group-hover:from-brandBlue/10 group-hover:to-brandPurple/10",
                "flex items-center justify-center"
              )}>
                <IconComponent 
                  className="text-brandBlue dark:text-brandPurple" 
                  size={isMobile ? 20 : 28} 
                />
              </div>
              <div className="flex flex-row items-center gap-2">
                <h3 className="font-semibold text-sm md:text-lg text-foreground truncate">
                  {app.display_name || app.name || 'Untitled'}
                </h3>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Move button for pinned cards */}
              {isPinnedPreview && dragHandleRef && (
                <div
                  ref={dragHandleRef}
                  {...dragListeners}
                  {...dragAttributes}
                  className="cursor-grab active:cursor-grabbing touch-none select-none"
                  style={{ 
                    // Ensure hover events pass through to button
                    pointerEvents: 'auto'
                  }}
                >
                  <MoveCardButton
                    variant="glass"
                    size="sm"
                    iconType="auto"
                    className="opacity-70 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      // Drag functionality is handled by the drag handle wrapper
                    }}
                  />
                </div>
              )}
              <div className="ml-auto"></div>
            </div>
          </div>
        
          <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-4 line-clamp-1 md:line-clamp-2 flex-1">
            {app.description}
          </p>

          {/* App Idea Development Progress Bar - Only for App Ideas */}
          {isAppIdea && (
            <div className="mb-3 space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className={cn(
                  "font-medium",
                  isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
                )}>
                  {isReadyForDevelopment ? "Ready for Development! 🎉" : "Proposal Progress"}
                </span>
                <span className={cn(
                  "font-semibold",
                  isReadyForDevelopment ? "text-green-600 dark:text-green-400" : "text-orange-600 dark:text-orange-400"
                )}>
                  {favoriteCount}/{DEVELOPMENT_THRESHOLD} likes
                </span>
              </div>
              <Progress 
                value={progressPercentage} 
                className="h-2"
                indicatorClassName={cn(
                  "transition-all duration-500",
                  isReadyForDevelopment 
                    ? "bg-gradient-to-r from-green-500 to-emerald-500" 
                    : "bg-gradient-to-r from-orange-400 to-amber-500"
                )}
              />
              {!isReadyForDevelopment && likesToGo > 0 && (
                <p className="text-xs text-muted-foreground">
                  {likesToGo} more {likesToGo === 1 ? 'like' : 'likes'} needed to start development
                </p>
              )}
              {isReadyForDevelopment && (
                <p className="text-xs text-green-600 dark:text-green-400 font-medium">
                  This app idea will be developed for the community!
                </p>
              )}
            </div>
          )}

          <div className="flex items-end justify-between mt-auto">
            <div className="flex items-center gap-2 mt-2">
              {/* Favorites button */}
              {app.id && !favoritesLoading && (
                <PersistentFavoriteButton 
                  itemId={app.id} 
                  onToggleFavorite={handleToggleAppFavorite}
                  isFavorite_prop={isFavorited}
                  favoriteCount_prop={favoriteCount}
                  data-test-id={`app-favorite-button-${app.id}`}
                  className="p-0"
                />
              )}
              
              {/* Pin position badge next to favorites */}
              {getPinPositionBadge()}
              
              {/* Pin dropdown menu - positioned next to favorites for consistency */}
              {isPinModeActive && !isPinned && app.status === 'active' && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <motion.button 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                      whileHover={{
                        scale: 1.05,
                        filter: 'brightness(0.95)',
                        transition: { type: "spring", stiffness: 400, damping: 25 }
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center justify-center">
                        <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
                      </div>
                      <ChevronsUpDown size={14} className="text-brandBlue dark:text-brandPurple" />
                    </motion.button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48 z-50">
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePinApp(app.id, 1);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg">
                          <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
                          <span className="text-sm font-medium text-white">1st</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePinApp(app.id, 2);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg">
                          <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
                          <span className="text-sm font-medium text-white">2nd</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePinApp(app.id, 3);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg">
                          <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
                          <span className="text-sm font-medium text-white">3rd</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePinApp(app.id, 4);
                      }}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 dark:bg-black/20 border border-white/20 dark:border-transparent shadow-lg backdrop-blur-lg">
                          <Pin size={14} className="text-brandBlue dark:text-brandPurple" />
                          <span className="text-sm font-medium text-white">4th</span>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              
              {app.status === 'coming-soon' && (
                <GlassPill 
                  type="Coming Soon"
                  variant="amber"
                  className="text-xs font-medium shadow-sm"
                />
              )}
              
              {app.status === 'proposed' && (
                <GlassPill 
                  type="Proposed"
                  variant="amber"
                  className="text-xs font-medium shadow-sm"
                />
              )}
            </div>
            
            {/* Category tag on the right side */}
            <div className="flex items-center gap-2 mt-2">
              <div className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full",
                app.category === 'productivity' && "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
                app.category === 'creative' && "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
                app.category === 'education' && "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
                app.category === 'finance' && "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
                app.category === 'health' && "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
                app.category === 'entertainment' && "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
                app.category === 'shopping' && "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300"
              )}>
                {app.category.charAt(0).toUpperCase() + app.category.slice(1)}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default AppCard;
